/**
 * frequency-chart.component.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import {
  Component,
  DoCheck,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  ViewChild
} from "@angular/core";
import * as D3Axis from "d3-axis";
import * as D3Scale from "d3-scale";
import * as D3Selection from "d3-selection";
import { FrequencyData, FrequencyResult } from "../aggregation/typed-parameter";
import { DataType } from "../common/data/data-types";
import { TypedData } from "../common/data/typed-data";

interface Margins
{
  top : number;
  bottom : number;
  left : number;
  right : number;
}

@Component({
  selector: `frequency-chart`,
  template: `
  <!-- Note that if we don't use <svg> as the outermost tag, the angular schema
  parser may not automatically recognize the namespace. In that case we should
  explicitly declare the namespaces of tags, like so: <svg:line> -->
  <svg
    #svgCanvas
    [attr.viewbox]="'0 0 ' + outerWidth + ' ' + outerHeight"
    [attr.height]="outerHeight"
    preserveAspectRatio="xMidYMid meet">
    <g
      class="chart"
      [attr.transform]="'translate(' + margin.left + ', ' + margin.top + ')'">
      <g *ngIf="useBar">
        <g
          *ngFor="let item of data.frequencies"
          class="bar">
          <rect
            [attr.x]="x(item.element)"
            [attr.width]="x.bandwidth()"
            [attr.y]="y(item.frequency)"
            [attr.height]="height - y(item.frequency)">
          </rect>
        </g>
      </g>
      <g *ngIf="useScatter">
        <g class="dots">
          <circle
            *ngFor="let item of data.frequencies"
            class="dot"
            [attr.cx]="x(item.element)"
            [attr.cy]="y(item.frequency)"
            r="2">
          </circle>
        </g>
      </g>
      <text *ngIf="dataFailure" transform="'translate(' + this.width/2 + ',' + this.height/2 + ')">
        Too many entries to chart.
      </text>
      <g #xAxis [attr.transform]="'translate(0, ' + this.height + ')'"></g>
      <g #yAxis></g>
    </g>
  </svg>
  `,
  styles: [`
    svg {
      display: block;
      margin: auto;
      max-width: 100%;
      width: 100%;
      border: 1px solid black;
    }
    .bar rect {
      fill: steelblue;
      fill-opacity: 0.75;
    }
    circle.dot {
      fill: steelblue;
      fill-opacity: 0.4;
    }
  `]
})
export class FrequencyChartComponent implements OnInit, DoCheck
{
  public readonly aspectRatio : number = 4 / 6;
  public outerWidth : number;
  public outerHeight : number;
  public margin : Margins = {top: 20, right: 20, bottom: 30, left: 40};
  public get width () : number
  {
    return this.outerWidth - this.margin.left - this.margin.right;
  }
  public get height () : number
  {
    return this.outerHeight - this.margin.top - this.margin.bottom;
  }
  public x : D3Scale.ScaleBand<string> | D3Scale.ScaleLinear<number, number>;
  public y : D3Scale.ScaleLinear<number, number>;

  @Input()
  public data : FrequencyData;

  @ViewChild("svgCanvas")
  public svgCanvas : ElementRef;
  @ViewChild("xAxis")
  public xAxisElement : ElementRef;
  @ViewChild("yAxis")
  public yAxisElement : ElementRef;

  public xAxisCreator : D3Axis.Axis<string | number>;
  public yAxisCreator : D3Axis.Axis<number>;

  public useBar : boolean = false;
  public useScatter : boolean = false;
  public dataFailure : boolean = false;

  public ngOnInit () : void
  {
    if (this.data.frequencies.length > 20)
    {
      if (this.isNumeric(this.data.type))
      {
        this.useScatter = true;
      }
      else
      {
        this.dataFailure = true;
      }
    }
    else
    {
      this.useBar = true;
    }
    this.resize(600);
    this.readData();
    // TODO: eventually figure out why the rect size isn't updated until later
    setTimeout(() => this.ngDoCheck(), 1);
  }

  private isNumeric (type : DataType) : boolean
  {
    return ([
      "integer", "whole number", "natural number", "float", "date"
    ].indexOf(type) > -1);
  }

  @HostListener("window:resize")
  public ngDoCheck () : void
  {
    const svgRect : ClientRect =
      (this.svgCanvas.nativeElement as HTMLElement).getBoundingClientRect();
    if (svgRect.width !== this.outerWidth)
    {
      console.log("width change " + this.outerWidth + "→" + svgRect.width);
      this.resize(svgRect.width);
      this.readData();
    }
  }

  private resize (width : number) : void
  {
    if (width === 0)
    {
      return;
    }
    this.outerWidth = width;
    this.outerHeight = this.aspectRatio * width;
    if (this.useScatter)
    {
      this.x = D3Scale.scaleLinear().range([0, this.width]);
    }
    else
    {
      this.x = D3Scale.scaleBand()
        .rangeRound([0, this.width])
        .paddingInner(0.1);
    }
    this.y = D3Scale.scaleLinear().range([this.height, 0]);
  }

  private readData () : void
  {
    if (!this.data)
    {
      return console.warn("Requested chart data read before data ready!");
    }
    const maxFreq : number = this.data.frequencies.map(
      (e : {frequency : number}) => e.frequency
    ).reduce((a : number, b : number) => Math.max(a, b), 0);
    if (this.useScatter)
    {
      const xDomain : [number, number] = this.data.frequencies.map(
        (e : {element : number}) => e.element
      ).reduce(
        ([min, max] : [number, number], b : number) =>
          [Math.min(min, b), Math.max(max, b)],
        [Number.MAX_SAFE_INTEGER, Number.MIN_SAFE_INTEGER]
      ) as [number, number];
      (this.x as D3Scale.ScaleLinear<number, number>).domain(xDomain);
      this.xAxisCreator = D3Axis.axisBottom<number>(
          this.x as D3Scale.ScaleLinear<number, number>);
    }
    else
    {
      (this.x as D3Scale.ScaleBand<string>).domain(this.data.frequencies.map(
        ({element} : {element : any}) => element));
      this.xAxisCreator = D3Axis.axisBottom<any>(this.x);
    }
    if (this.data.type === "date")
    {
      this.xAxisCreator
        .ticks(Math.min(this.data.frequencies.length, 7))
        .tickFormat(
          (x : number) => (new Date(x)).toLocaleDateString());
    }
    this.y.domain([0, maxFreq]);
    this.yAxisCreator = D3Axis.axisLeft<number>(this.y)
      .ticks(Math.min(maxFreq, 11))
      .tickFormat((val : number) => `${val}`);
    this.xAxisCreator(D3Selection.select(this.xAxisElement.nativeElement));
    this.yAxisCreator(D3Selection.select(this.yAxisElement.nativeElement));
  }

  public afterSort () : void
  {
    console.log("Rereading data...");
    this.readData();
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/graphs/frequency-chart.component.ts